import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { emitProfileChange } from "@/lib/profileChangeEmitter";

export interface AthleteProfileData {
  athlete_type_id: string | null;
  athlete_type_confidence: number;
  athlete_type_reasons: string[];
  athlete_profile_json: Record<string, unknown>;
  quiz_answers_json: Record<string, unknown>;
  user_objectives: string[];
  user_objectives_custom: string[];
  user_sports: string[];
  sports_custom: string[];
  onboarding_completed_at: string | null;
  profile_history: Record<string, unknown>[];
}

const defaultData: AthleteProfileData = {
  athlete_type_id: null,
  athlete_type_confidence: 0,
  athlete_type_reasons: [],
  athlete_profile_json: { morphotype_use_enabled: true },
  quiz_answers_json: {},
  user_objectives: [],
  user_objectives_custom: [],
  user_sports: [],
  sports_custom: [],
  onboarding_completed_at: null,
  profile_history: [],
};

/** Stable query key — also invalidated by useProfileChangeInvalidation */
export const ATHLETE_PROFILE_QUERY_KEY = (userId: string) => ["athlete-profile", userId];

function rowToData(row: any): AthleteProfileData {
  return {
    athlete_type_id: row.athlete_type_id ?? null,
    athlete_type_confidence: Number(row.athlete_type_confidence) || 0,
    athlete_type_reasons: row.athlete_type_reasons ?? [],
    athlete_profile_json: {
      morphotype_use_enabled: true,
      ...(row.athlete_profile_json ?? {}),
    },
    quiz_answers_json: row.quiz_answers_json ?? {},
    user_objectives: row.user_objectives ?? [],
    user_objectives_custom: row.user_objectives_custom ?? [],
    user_sports: row.user_sports ?? [],
    sports_custom: row.sports_custom ?? [],
    onboarding_completed_at: row.onboarding_completed_at ?? null,
    profile_history: Array.isArray(row.profile_history) ? row.profile_history : [],
  };
}

async function fetchAthleteProfile(userId: string): Promise<AthleteProfileData> {
  const { data: row } = await supabase
    .from("athlete_profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  return row ? rowToData(row) : defaultData;
}

export function useAthleteProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data = defaultData, isLoading: loading } = useQuery({
    queryKey: userId ? ATHLETE_PROFILE_QUERY_KEY(userId) : ["athlete-profile", "anon"],
    queryFn: () => fetchAthleteProfile(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });

  const isOnboardingComplete = data.onboarding_completed_at !== null;

  // ── saveDraft mutation ──
  const saveDraftMutation = useMutation({
    mutationFn: async (patch: Partial<AthleteProfileData>) => {
      if (!userId) throw new Error("No user");
      const payload = { user_id: userId, ...patch };
      const { error } = await supabase
        .from("athlete_profiles")
        .upsert(payload as any, { onConflict: "user_id" });
      if (error) throw error;
      return patch;
    },
    onSuccess: (patch) => {
      if (userId) {
        queryClient.setQueryData(ATHLETE_PROFILE_QUERY_KEY(userId), (old: AthleteProfileData | undefined) => ({
          ...(old ?? defaultData),
          ...patch,
        }));
      }
      emitProfileChange();
    },
  });

  // ── completeOnboarding mutation ──
  const completeOnboardingMutation = useMutation({
    mutationFn: async (patch: Partial<AthleteProfileData>) => {
      if (!userId) throw new Error("No user");
      const now = new Date().toISOString();
      const payload = { user_id: userId, ...patch, onboarding_completed_at: now };
      const { error } = await supabase
        .from("athlete_profiles")
        .upsert(payload as any, { onConflict: "user_id" });
      if (error) throw error;
      return { ...patch, onboarding_completed_at: now };
    },
    onSuccess: (merged) => {
      if (userId) {
        queryClient.setQueryData(ATHLETE_PROFILE_QUERY_KEY(userId), (old: AthleteProfileData | undefined) => ({
          ...(old ?? defaultData),
          ...merged,
        }));
      }
      emitProfileChange();
    },
  });

  // ── setMorphotypeUseEnabled ──
  const morphotypeMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!userId) throw new Error("No user");
      const newApj = { ...data.athlete_profile_json, morphotype_use_enabled: enabled };
      const { error } = await supabase
        .from("athlete_profiles")
        .update({ athlete_profile_json: newApj } as any)
        .eq("user_id", userId);
      if (error) throw error;
      return newApj;
    },
    onSuccess: (newApj) => {
      if (userId) {
        queryClient.setQueryData(ATHLETE_PROFILE_QUERY_KEY(userId), (old: AthleteProfileData | undefined) => ({
          ...(old ?? defaultData),
          athlete_profile_json: newApj,
        }));
      }
    },
  });

  const saveDraft = useCallback(
    async (patch: Partial<AthleteProfileData>) => { await saveDraftMutation.mutateAsync(patch); },
    [saveDraftMutation]
  );

  const completeOnboarding = useCallback(
    async (patch: Partial<AthleteProfileData>) => { await completeOnboardingMutation.mutateAsync(patch); },
    [completeOnboardingMutation]
  );

  const setMorphotypeUseEnabled = useCallback(
    async (enabled: boolean) => { await morphotypeMutation.mutateAsync(enabled); },
    [morphotypeMutation]
  );

  // setData kept for rare imperative overrides
  const setData = useCallback(
    (updater: AthleteProfileData | ((prev: AthleteProfileData) => AthleteProfileData)) => {
      if (!userId) return;
      queryClient.setQueryData(ATHLETE_PROFILE_QUERY_KEY(userId), (old: AthleteProfileData | undefined) => {
        const prev = old ?? defaultData;
        return typeof updater === "function" ? updater(prev) : updater;
      });
    },
    [userId, queryClient]
  );

  return { data, loading, isOnboardingComplete, saveDraft, completeOnboarding, setData, setMorphotypeUseEnabled };
}
